import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Info, Settings2 } from "lucide-react";
import { Role } from "@/types/roles";
import { PermissionsSelector } from "./PermissionsSelector";

/**
 * Convierte los permisos de Role a un arreglo de strings.
 */
const normalizePermissions = (permissions: Role["permissions"]): string[] => {
  if (Array.isArray(permissions)) return permissions;

  if (typeof permissions === "object" && permissions !== null) {
    if (permissions.all === true) return ["*"];

    return Object.entries(permissions)
      .filter(([_, value]) => value === true)
      .map(([key]) => key);
  }

  return [];
};

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (roleData: Partial<Role>) => Promise<void>;
  role?: Role | null;
  companySlug: string;
  companyId: string;
  loading?: boolean;
}

export function RoleModal({
  isOpen,
  onClose,
  onSave,
  role,
  companySlug,
  companyId,
  loading = false,
}: RoleModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ name?: string; permissions?: string }>(
    {}
  );

  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description || "");
      setSelectedPermissions(normalizePermissions(role.permissions));
    } else {
      setName("");
      setDescription("");
      setSelectedPermissions([]);
    }
    setErrors({});
  }, [role, isOpen]);

  const validateForm = () => {
    const newErrors: typeof errors = {};
    if (!name.trim()) newErrors.name = "El nombre del rol es requerido";
    if (selectedPermissions.length === 0)
      newErrors.permissions = "Debe seleccionar al menos un permiso";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const permissionsValue = selectedPermissions.includes("*")
        ? { all: true }
        : selectedPermissions;

      const roleData: Partial<Role> = {
        name: name.trim(),
        description: description.trim(),
        permissions: permissionsValue,
        slug: companySlug,
        company_id: companyId,
        is_system_role: role?.is_system_role || false,
      };

      await onSave(roleData);
      onClose();
    } catch (error) {
      console.error("Error al guardar rol:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={loading ? undefined : onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold">
            <Shield className="h-5 w-5 text-blue-600" />
            {role ? "Configurar Rol" : "Crear Nuevo Rol"}
          </DialogTitle>
          <DialogDescription>
            {role
              ? "Actualiza los detalles y permisos de acceso para este perfil."
              : "Define un nuevo perfil de acceso y sus permisos asociados."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* SECCIÓN 1: DATOS BÁSICOS */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="bg-slate-50/50 py-3 border-b">
              <CardTitle className="text-sm font-bold flex items-center gap-2 text-slate-700">
                <Info className="h-4 w-4 text-blue-500" />
                Información del Rol
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs font-bold text-slate-500">NOMBRE DEL ROL *</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ej: Gerente de Ventas"
                    className={errors.name ? "border-red-500 bg-red-50" : "bg-white"}
                    disabled={loading}
                  />
                  {errors.name && (
                    <p className="text-[10px] text-red-500 font-medium">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-xs font-bold text-slate-500">DESCRIPCIÓN</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe las facultades de este rol..."
                    rows={1}
                    className="min-h-[44px] h-11 resize-none bg-white"
                    disabled={loading}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SECCIÓN 2: PERMISOS */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="bg-slate-50/50 py-3 border-b">
              <CardTitle className="text-sm font-bold flex items-center gap-2 text-slate-700">
                <Settings2 className="h-4 w-4 text-blue-500" />
                Configuración de Permisos
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <PermissionsSelector
                selectedPermissions={selectedPermissions}
                onPermissionsChange={setSelectedPermissions}
                error={errors.permissions}
              />
              <p className="text-[10px] text-slate-400 mt-4 italic">
                * El acceso total ("*") otorga control absoluto sobre todas las funciones de la plataforma.
              </p>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="border-t pt-6">
          <Button variant="outline" onClick={onClose} disabled={loading} className="rounded-lg">
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={loading} className="bg-blue-600 hover:bg-blue-700 rounded-lg px-8">
            {loading ? "Procesando..." : role ? "Guardar Cambios" : "Crear Rol"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
